import { useCallback, useEffect, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { pushStepAtom, signUpDataAtom, SignUpData } from "store/signUpStore";
import { userStore } from "store/userStore";
import { useCheckDuplicateCtn } from "hooks/useCheckDuplicateCtn";

/**
 * 가입 연령 정책 (서버 판정) 흐름
 *
 * - check(birth, onLegacy): 생년월일 입력 직후 서버 판정
 *     정책 미적용 응답 또는 조회 실패 → onLegacy() (기존 흐름 그대로)
 *     가입 불가 → Step10 / 그 외 → 본인 정보·보호자 입력(Step11)
 * - routeToAuth(data): 본인 정보(·보호자 번호) 입력 뒤 인증 단계로 이동
 *     본인인증 사용(idvEnabled) → 기존 다날 흐름 그대로 (동의 대상 KR 이면 기존 보호자 다날 회차)
 *     본인인증 미사용            → 휴대폰 인증(Step4 → Step5 SMS)
 *
 * 판정은 가입 API 에서도 서버가 다시 한다 — 화면 판정은 안내·흐름용일 뿐이다.
 */
export const useSignupPolicy = () => {
    const pushStep = useSetAtom(pushStepAtom);
    const setSignUpData = useSetAtom(signUpDataAtom);
    const checkState = useAtomValue(userStore.signupPolicyCheck.stateAtom);
    const { navigateToAuth } = useCheckDuplicateCtn();
    const [isChecking, setIsChecking] = useState(false);
    const onLegacyRef = useRef<() => void>(() => {});

    useEffect(() => {
        if (!isChecking) return;
        if (checkState.status === "ready") {
            setIsChecking(false);
            const data = checkState.data || {};
            userStore.signupPolicyCheck.onDefault();
            // 정책 미적용(기능 꺼짐·정책 없는 서비스) — 기존 흐름
            if (data.policyEnabled !== "Y") {
                setSignUpData(prev => ({ ...prev, policyEnabled: false }));
                onLegacyRef.current();
                return;
            }
            const result: string | undefined = data.result;
            const consentAge = data.consentAge;
            if (result === "MINOR_NOT_ALLOWED" || result === "AGE_BELOW_SERVICE_MIN") {
                setSignUpData(prev => ({
                    ...prev,
                    policyEnabled: true,
                    policyBlockReason: result,
                    consentAge: consentAge ?? prev.consentAge,
                }));
                pushStep(10);
                return;
            }
            const guardianRequired = result === "GUARDIAN_REQUIRED";
            setSignUpData(prev => ({
                ...prev,
                policyEnabled: true,
                guardianRequired,
                idvEnabled: data.idvEnabled !== "N",
                consentAge: consentAge ?? prev.consentAge,
                policyBlockReason: "",
                // 동의 대상이 아니면 이전에 입력한 보호자 정보를 남기지 않는다 (생년월일을 고친 경우)
                ...(guardianRequired ? {} : {
                    guardianName: "", guardianRelationship: "", guardianCtn: "", guardianCtnCountryCd: "",
                }),
            }));
            pushStep(11);
        } else if (checkState.status === "error") {
            // 판정 실패 — 기존 흐름으로 진행 (서버가 가입 시 다시 판정한다).
            //   정책 화면 상태를 끄지 않으면 진입 조회값(policyEnabled)이 남아 기존 흐름인데 정책 필드가 실려 가입이 거부된다.
            setIsChecking(false);
            userStore.signupPolicyCheck.onDefault();
            setSignUpData(prev => ({ ...prev, policyEnabled: false }));
            onLegacyRef.current();
        }
    }, [checkState.status, checkState.data, isChecking, pushStep, setSignUpData]);

    const check = useCallback((params: { serviceId: string; birth: string; countryCd?: string; ctnCountryCd?: string },
                               onLegacy: () => void) => {
        onLegacyRef.current = onLegacy;
        userStore.signupPolicyCheck.onDefault();
        setIsChecking(true);
        userStore.onCheckSignupPolicy(params);
    }, []);

    const routeToAuth = useCallback((data: SignUpData) => {
        if (data.idvEnabled) {
            if (data.guardianRequired && data.countryCd === "KR") {
                // 기존 보호자 다날 흐름: Step2(보호자 생년월일) → Step3(보호자 이름·통신사·번호) → Step5(다날)
                //   보호자 번호는 Step3 에서 받고, 다날 인증 성공 시 guardian 정보로 되돌려 쓴다 (Step5).
                setSignUpData(prev => ({
                    ...prev,
                    parentConsent: true,
                    parentName: prev.guardianName,
                }));
                pushStep(2);
                return;
            }
            navigateToAuth(data.countryCd);
            return;
        }
        // 본인인증 미사용 — 국내·해외 모두 휴대폰 인증
        pushStep(4);
    }, [navigateToAuth, pushStep, setSignUpData]);

    return { check, routeToAuth, isChecking };
};
